import React, { useEffect, useMemo, useState } from "react";
import { Spin, Tag } from "antd";
import { useSelector } from "react-redux";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { useDispatch } from "react-redux";

import BoardColumn from "./BoardColumn";
import * as issuesApi from "../../../../core/data/redux/services/issuesApi";
import { moveIssue } from "../../../../core/data/redux/projects/thunks";
import {
  selectActiveCompany,
  selectActiveSprint,
} from "../../../../core/data/redux/projects/selectors";

import type {
  Issue,
  Status,
} from "../../../../core/data/redux/projects/types";
import type { AppDispatch } from "../../../../core/data/redux/store";

interface BoardSwimlanesProps {
  projectId: number;
  projectKey: string;
  statuses: Status[];
  issues: Issue[];
  onCreateIssueClick: (statusId: number, epicId: number | null) => void;
}

interface EpicSwimlane {
  epicId: number | "NONE";
  epicKey: string;
  epicTitle: string;
  epicColor: string | null;
}

const BoardSwimlanes: React.FC<BoardSwimlanesProps> = ({
  projectId,
  projectKey,
  statuses,
  issues,
  onCreateIssueClick,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const activeCompany = useSelector(selectActiveCompany);
  const activeSprint = useSelector(selectActiveSprint);

  const [epics, setEpics] = useState<Issue[]>([]);
  const [loading, setLoading] = useState(false);
  const [optimistic, setOptimistic] = useState<Issue[] | null>(null);
  const [activeIssue, setActiveIssue] = useState<Issue | null>(null);

  useEffect(() => {
    if (!activeCompany.company_id) return;
    let cancelled = false;
    setLoading(true);
    const filters: Record<string, any> = {
      project_id: projectId,
      type: "EPIC",
      limit: 200,
    };
    if (activeSprint?.id) filters.sprint_id = activeSprint.id;
    issuesApi
      .listIssues(activeCompany.company_id, activeCompany.company_type, filters)
      .then((data: any) => {
        if (cancelled) return;
        setEpics(Array.isArray(data?.issues) ? data.issues : []);
      })
      .catch(() => {
        if (!cancelled) setEpics([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    projectId,
    activeCompany.company_id,
    activeCompany.company_type,
    activeSprint?.id,
  ]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const sortedStatuses = useMemo(
    () => [...statuses].sort((a, b) => a.position - b.position),
    [statuses]
  );

  const issuesToShow = optimistic || issues;

  const swimlanes: EpicSwimlane[] = useMemo(() => {
    const list: EpicSwimlane[] = epics.map((e) => ({
      epicId: Number(e.id),
      epicKey: e.issue_key,
      epicTitle: e.title,
      epicColor: e.epic_color,
    }));
    list.push({
      epicId: "NONE",
      epicKey: "—",
      epicTitle: "No Epic",
      epicColor: null,
    });
    return list;
  }, [epics]);

  const issuesByLane = useMemo(() => {
    const map = new Map<
      number | "NONE",
      Map<number, Issue[]>
    >();
    swimlanes.forEach((lane) => {
      const inner = new Map<number, Issue[]>();
      sortedStatuses.forEach((s) => inner.set(Number(s.id), []));
      map.set(lane.epicId, inner);
    });
    for (const issue of issuesToShow) {
      if (!issue.is_active) continue;
      if (issue.type === "EPIC") continue;
      const laneKey: number | "NONE" =
        issue.epic_id == null ? "NONE" : Number(issue.epic_id);
      const lane = map.get(laneKey);
      if (!lane) continue;
      const list = lane.get(Number(issue.status_id));
      if (list) list.push(issue);
    }
    map.forEach((lane) => {
      lane.forEach((list) =>
        list.sort((a, b) => (a.rank < b.rank ? -1 : a.rank > b.rank ? 1 : 0))
      );
    });
    return map;
  }, [issuesToShow, swimlanes, sortedStatuses]);

  const findIssueByDragId = (dragId: string): Issue | undefined => {
    const numId = Number(dragId.replace("issue-", ""));
    return issuesToShow.find((i) => Number(i.id) === numId);
  };

  const handleDragStart = (event: DragStartEvent) => {
    const id = String(event.active.id);
    if (id.startsWith("issue-")) {
      const found = findIssueByDragId(id);
      if (found) setActiveIssue(found);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    setActiveIssue(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const dragged = findIssueByDragId(String(active.id));
    if (!dragged) return;

    const overId = String(over.id);
    let newStatusId: number | null = null;
    let beforeId: number | null = null;
    let afterId: number | null = null;

    if (overId.startsWith("column-")) {
      newStatusId = Number(overId.replace("column-", "").split("-")[0]);
    } else if (overId.startsWith("issue-")) {
      const overIssue = findIssueByDragId(overId);
      if (overIssue) {
        newStatusId = Number(overIssue.status_id);
        const lane = (
          dragged.epic_id == null ? "NONE" : Number(dragged.epic_id)
        ) as number | "NONE";
        const overLane = (
          overIssue.epic_id == null ? "NONE" : Number(overIssue.epic_id)
        ) as number | "NONE";
        if (lane !== overLane) {
          afterId = Number(overIssue.id);
        } else {
          const list =
            issuesByLane.get(overLane)?.get(newStatusId) || [];
          const idx = list.findIndex((i) => Number(i.id) === Number(overIssue.id));
          beforeId =
            idx > 0 && Number(list[idx - 1].id) !== Number(dragged.id)
              ? Number(list[idx - 1].id)
              : null;
          afterId = Number(overIssue.id);
        }
      }
    }

    if (newStatusId == null) return;

    const opt = issuesToShow.map((i) =>
      Number(i.id) === Number(dragged.id) ? { ...i, status_id: newStatusId as number } : i
    );
    setOptimistic(opt);

    try {
      await dispatch(
        moveIssue({
          id: Number(dragged.id),
          status_id: newStatusId,
          sprint_id: activeSprint ? activeSprint.id : null,
          before_id: beforeId ? Number(beforeId) : null,
          after_id: afterId ? Number(afterId) : null,
        })
      ).unwrap();
    } catch {
      // revert via optimistic clear
    } finally {
      setOptimistic(null);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spin />
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActiveIssue(null)}
    >

      <div className="d-flex flex-column gap-3">
        {swimlanes.map((lane) => {
          const laneMap = issuesByLane.get(lane.epicId);
          if (!laneMap) return null;
          const totalInLane = sortedStatuses.reduce(
            (sum, s) => sum + (laneMap.get(Number(s.id))?.length || 0),
            0
          );
          if (lane.epicId === "NONE" && totalInLane === 0) return null;
          return (
            <div
              key={lane.epicId}
              className="card"
              style={{
                borderLeft: `4px solid ${lane.epicColor || "#9CA3AF"}`,
              }}
            >
              <div className="card-header bg-white d-flex align-items-center gap-2">
                <Tag color="purple" style={{ margin: 0 }}>
                  {lane.epicKey}
                </Tag>
                <span className="fw-semibold">{lane.epicTitle}</span>
                <span className="text-muted small ms-auto">
                  {totalInLane} issue{totalInLane === 1 ? "" : "s"}
                </span>
              </div>
              <div
                className="card-body p-3 bg-white rounded-3"
                style={{ overflowX: "auto", overflowY: "visible" }}
              >
                <div className="d-flex gap-3">
                  {sortedStatuses.map((status) => (
                    <BoardColumn
                      key={status.id}
                      status={status}
                      issues={laneMap.get(Number(status.id)) || []}
                      projectKey={projectKey}
                      onCreateIssueClick={(sid) =>
                        onCreateIssueClick(
                          sid,
                          lane.epicId === "NONE" ? null : (lane.epicId as number)
                        )
                      }
                    />
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <DragOverlay dropAnimation={{ duration: 180 }}>
        {activeIssue && (
          <div
            className="card border-0 px-3 py-2"
            style={{
              borderRadius: 8,
              minWidth: 240,
              maxWidth: 320,
              boxShadow: "0 18px 36px rgba(59,130,246,0.35)",
              background: "#fff",
              borderLeft: "3px solid #3b82f6",
              cursor: "grabbing",
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 600 }}>{activeIssue.title}</div>
            <div className="small text-primary fw-semibold mt-1">{activeIssue.issue_key}</div>
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
};

export default BoardSwimlanes;
